import BI from '@/api/modules/budget_insight'
import rg4js from 'raygun4js'
import { formatIncomingAccount } from '@/utils/budget-insight'
import Vue from 'vue'

const ADD_WEBAUTH_CODE = 'ADD_WEBAUTH_CODE'
const ADD_BI_USER = 'ADD_BI_USER'
const ADD_TOKEN = 'ADD_TOKEN'

const getDefaultState = () => {
  return {
    BIToken: null,
    BIWebauthCode: null,
    BIUser: null,
    BIContractList: null,
    BIContractListFormatted: null,
    BITermsStatus: null,
    BIAcceptedTerms: false
  }
}

const state = getDefaultState()

const actions = {
  fetchWebauthCode (context, { token }) {
    return BI.fetchWebauthCode(token)
      .then((res) => {
        context.commit('ADD_WEBAUTH_CODE', res.code)
        return Promise.resolve(res.code)
      })
      .catch(({ response }) => {
        rg4js('send', new Error('Failed getting BIWebauthToken'))
        return Promise.reject(response)
      })
  },

  fetchBIToken (context, cached = false) {
    if (cached && context.state.BIToken) {
      const now = new Date()
      const expirationDate = new Date(context.state.BIToken.expiredAt)
      const validityThreshold = new Date(expirationDate.getTime() - 60000 * 5)

      if (now < validityThreshold) {
        return Promise.resolve(context.state.BIToken)
      }
    }

    return BI.fetchBIToken()
      .then((res) => {
        context.commit(ADD_TOKEN, res)
        return Promise.resolve(res)
      })
      .catch(({ response }) => {
        rg4js('send', new Error('Failed getting BITOKEN'))
        return Promise.reject(response)
      })
  },

  confirmBIToken (context, data) {
    return BI.confirmBIToken(data)
      .catch(({ response }) => {
        return Promise.reject(response)
      })
  },

  fetchBITermsStatus (context, { token, host, cached = true }) {
    if (cached && context.state.BITermsStatus) {
      return Promise.resolve()
    }

    return BI.fetchBITermsStatus(token, host)
      .then((res) => {
        context.commit('addTermStatus', res)

        if (res.toSign) {
          context.commit('updateTermsStatus', false)
        } else if (!res.terms) {
          context.commit('updateTermsStatus', true)
        }
      })
      .catch(({ response }) => {
        return Promise.reject(response)
      })
  },

  fetchBITerms (context, data) {
    return BI.fetchBITerms(data.token, data.host)
      .then((res) => {
        return (res)
      })
      .catch(({ response }) => {
        return Promise.reject(response)
      })
  },

  acceptBITerms (context, data) {
    return BI.acceptBITerms(data.token, data.host, data.termsId)
      .then((res) => {
        if (res.success === 'ok') {
          context.commit('updateTermsStatus', true)
        }
      })
      .catch(({ response }) => {
        return Promise.reject(response)
      })
  },

  getBIConnection (context, { token, idConnection, cached = true }) {
    if (cached && this.BIUser) {
      console.log(('cached'))
      Promise.resolve(this.BIUser)
    }

    return BI.getBIConnection(token, idConnection)
      .then((res) => {
        context.commit(ADD_BI_USER, res)
        return res
      })
      .catch(({ response }) => {
        return Promise.reject(response)
      })
  },

  fetchBIUser (context, data) {
    return BI.fetchBIUser(data)
      .then((res) => {
        context.commit(ADD_BI_USER, res)
        return res
      })
      .catch(({ response }) => {
        return Promise.reject(response)
      })
  },

  fetchBIContractList (context, data) {
    return BI.fetchBIContractList(data.token, data.host, data.BIUserID)
      .then((res) => {
        context.commit('addContracts', res)
      })
      .catch(({ response }) => {
        return Promise.reject(response)
      })
  },

  fetchBIAllContractList (context, { token, host, cached = true }) {
    if (cached && context.state.BIContractList) {
      return Promise.resolve()
    }

    return BI.fetchBIAllContractList(token, host)
      .then((res) => {
        context.commit('clearContracts')
        context.commit('addContracts', res)
        context.dispatch('formatBIContractList', res)
        return Promise.resolve()
      })
      .catch(({ response }) => {
        return Promise.reject(response)
      })
  },

  formatBIContractList ({ context, commit, rootState }, data) {
    let incomingAccounts = formatIncomingAccount(data.accounts, rootState.accounts.originalList)
    commit('addContractsFormatted', incomingAccounts)
  },

  activateBIContract (context, data) {
    return BI.activateBIContract(data.token, data.host, data.id)
      .then((res) => {
        return Promise.resolve(res)
      })
      .catch(({ response }) => {
        return Promise.reject(response)
      })
  },

  deactivateBIContract (context, data) {
    return BI.deactivateBIContract(data.token, data.host, data.id)
      .then((res) => {
        return Promise.resolve(res)
      })
      .catch(({ response }) => {
        return Promise.reject(response)
      })
  },

  updateBIAccountPassword (context, data) {
    return BI.updateBIAccountPassword(data)
      .then((res) => {
        return Promise.resolve(res)
      })
      .catch(({ response }) => {
        return Promise.reject(response)
      })
  },

  deleteBIAccount (context, id) {
    return BI.deleteBIAccount(id)
      .then(() => {
        context.commit('deleteAccountFormatted', id)
        return Promise.resolve()
      })
      .catch(({ response }) => {
        return Promise.reject(response)
      })
  },

  resetBIState (context) {
    context.commit('resetState')
  }
}

const mutations = {
  [ADD_WEBAUTH_CODE] (state, data) {
    Vue.set(state, 'BIWebauthCode', data)
  },

  [ADD_TOKEN] (state, data) {
    Vue.set(state, 'BIToken', data)
  },

  addTermStatus (state, data) {
    Vue.set(state, 'BITermsStatus', data)
  },

  updateTermsStatus (state, data) {
    Vue.set(state, 'BIAcceptedTerms', data)
  },

  [ADD_BI_USER] (state, data) {
    Vue.set(state, 'BIUser', data)
  },

  addContracts (state, data) {
    Vue.set(state, 'BIContractList', data)
  },

  addContractsFormatted (state, data) {
    Vue.set(state, 'BIContractListFormatted', data)
  },

  deleteAccountFormatted (state, id) {
    let indexOfId = state.BIContractListFormatted.findIndex(account => account.id === id)
    Vue.delete(state.BIContractListFormatted, indexOfId)
  },

  clearContracts (state) {
    Vue.delete(state, 'BIContractList')
    Vue.delete(state, 'BIContractListFormatted')
  },

  resetState (state) {
    Object.assign(state, getDefaultState())
  }
}

export default () => ({
  namespaced: true,
  state,
  actions,
  mutations
})
